import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { Virtuoso } from "react-virtuoso";
import { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { GfFlyerReviewChangeRequest } from "@swagger/typescript-fetch-goflyer";
import { messages } from "../../../../../locales/messages";
import { ReviewChangeRequest } from "../../types";
import { reviewTokens } from "../../theme";
import { isRestorable } from "./restoreEligibility";
import { resolveRestoreTooltip } from "./restoreReasonMessages";
import { CrSubmitterResolver } from "./useCrSubmitter";
import { SubmitterPill } from "./SubmitterPill";
import { HistorySlot } from "./buildHistorySlots";
import { absoluteTime, relativeFromNow } from "../CommentsPanel/time";
import { CrStatusChip, statusDotColor } from "./shared/CrStatusChip";
import { RevertChips } from "./shared/RevertChips";
import { formatValue } from "./shared/ValuePane";
import {
  isPromotionTagField,
  resolvePromotionTagNames,
} from "./shared/promotionTagDisplay";
import { usePromotionTags } from "../../../../../hooks/usePromotionTags";
import { GfPromotionTag } from "@swagger/typescript-fetch-goflyer";

interface Props {
  slots: HistorySlot[];
  flyerPublicationStatus?: string;
  resolveSubmitter: CrSubmitterResolver;
  busyIds: string[];
  onAccept: (cr: ReviewChangeRequest) => void;
  onReject: (cr: ReviewChangeRequest) => void;
  onRestore: (cr: ReviewChangeRequest) => void;
  emptyMessage?: string;
}

const St = GfFlyerReviewChangeRequest.StatusEnum;

// Compact value formatter for the history rail. Promotion-tag values are arrays
// of UUIDs, so resolve them to a comma-separated name list; everything else
// falls back to the generic scalar/JSON formatter.
const formatHistoryValue = (
  value: unknown,
  fieldKey: unknown,
  tags: GfPromotionTag[] | undefined,
): string => {
  if (isPromotionTagField(fieldKey)) {
    const names = resolvePromotionTagNames(value, tags);
    return names.length > 0 ? names.join(", ") : "No tags";
  }
  return formatValue(value);
};

// Per-item history is usually a handful of slots; only the cross-item
// FullHistoryDrawer realistically exceeds this and gets windowed.
const SLOT_VIRTUALIZE_THRESHOLD = 20;

const SLOT_RAIL_WIDTH = 24;
const NODE_DOT_SIZE = 12;
const NODE_GAP = 14;

interface NodeProps {
  cr: ReviewChangeRequest;
  isFirst: boolean;
  isLast: boolean;
  flyerPublicationStatus?: string;
  resolveSubmitter: CrSubmitterResolver;
  busy: boolean;
  onAccept: (cr: ReviewChangeRequest) => void;
  onReject: (cr: ReviewChangeRequest) => void;
  onRestore: (cr: ReviewChangeRequest) => void;
}

const HistoryNode = memo(function HistoryNode({
  cr,
  isFirst,
  isLast,
  flyerPublicationStatus,
  resolveSubmitter,
  busy,
  onAccept,
  onReject,
  onRestore,
}: NodeProps) {
  const { t } = useTranslation();
  const { promotionTags } = usePromotionTags();
  const ts = cr.submittedAt || cr.createdAt;
  const eligibility = isRestorable(cr, flyerPublicationStatus);
  const restoreTooltip = resolveRestoreTooltip(eligibility, t);
  const dotColor = statusDotColor(cr.status);
  const isRevertCr = !!cr.metadata?.restoreOf;
  const wasRevertedLater = !!cr.metadata?.restoredByCrId;

  return (
    <Stack
      direction="row"
      spacing={1.25}
      alignItems="stretch"
      data-testid={`history-node-${cr.id}`}
      data-cr-id={cr.id}
      sx={{ position: "relative", pb: `${NODE_GAP}px` }}
    >
      <Box
        sx={{
          width: SLOT_RAIL_WIDTH,
          flexShrink: 0,
          position: "relative",
        }}
      >
        {!isFirst ? (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              bottom: `calc(50% + ${NODE_DOT_SIZE / 2}px)`,
              left: "50%",
              width: 2,
              transform: "translateX(-1px)",
              bgcolor: reviewTokens.slate200,
            }}
          />
        ) : null}
        {!isLast ? (
          <Box
            sx={{
              position: "absolute",
              top: `calc(50% + ${NODE_DOT_SIZE / 2}px)`,
              bottom: 0,
              left: "50%",
              width: 2,
              transform: "translateX(-1px)",
              bgcolor: reviewTokens.slate200,
            }}
          />
        ) : null}
        <Box
          data-testid={`history-node-dot-${cr.id}`}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: NODE_DOT_SIZE,
            height: NODE_DOT_SIZE,
            borderRadius: "50%",
            bgcolor: dotColor,
            border: `2px solid ${reviewTokens.bgPaper}`,
            boxShadow: `0 0 0 2px ${dotColor}33`,
            zIndex: 1,
          }}
        />
      </Box>

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          border: `1px solid ${reviewTokens.border}`,
          borderRadius: `${reviewTokens.radiusMd}px`,
          bgcolor: reviewTokens.bgPaper,
          p: 1.25,
        }}
      >
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          flexWrap="wrap"
          sx={{ rowGap: 0.5, mb: 0.75 }}
        >
          <CrStatusChip status={cr.status} tone="dot" />
          <RevertChips
            isRevertCr={isRevertCr}
            wasRevertedLater={wasRevertedLater}
          />
          <SubmitterPill cr={cr} resolve={resolveSubmitter} />
          {ts ? (
            <Tooltip title={absoluteTime(ts)}>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary" }}
              >
                {relativeFromNow(ts)}
              </Typography>
            </Tooltip>
          ) : null}
        </Stack>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={0.5}
          alignItems={{ xs: "stretch", sm: "center" }}
          sx={{ mb: 0.5 }}
        >
          <Typography
            variant="caption"
            sx={{
              fontFamily: reviewTokens.fontMono,
              color: reviewTokens.slate500,
              flexShrink: 0,
            }}
          >
            {formatHistoryValue(cr.oldValue, cr.fieldKey, promotionTags)}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: reviewTokens.slate400, flexShrink: 0 }}
          >
            →
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontFamily: reviewTokens.fontMono,
              fontWeight: 500,
              color: "text.primary",
              wordBreak: "break-word",
            }}
          >
            {formatHistoryValue(cr.newValue, cr.fieldKey, promotionTags)}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
          {cr.status === St.PENDING ? (
            <>
              <Tooltip title={t(messages.Accept())}>
                <Box
                  component="button"
                  type="button"
                  disabled={busy}
                  onClick={() => onAccept(cr)}
                  data-testid={`history-accept-${cr.id}`}
                  sx={{
                    border: `1px solid ${reviewTokens.success}66`,
                    bgcolor: `${reviewTokens.success}14`,
                    color: reviewTokens.success,
                    borderRadius: `${reviewTokens.radiusSm}px`,
                    px: 0.75,
                    py: 0.25,
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: busy ? "not-allowed" : "pointer",
                    opacity: busy ? 0.5 : 1,
                  }}
                >
                  {t(messages.Accept())}
                </Box>
              </Tooltip>
              <Tooltip title={t(messages.Reject())}>
                <Box
                  component="button"
                  type="button"
                  disabled={busy}
                  onClick={() => onReject(cr)}
                  data-testid={`history-reject-${cr.id}`}
                  sx={{
                    border: `1px solid ${reviewTokens.danger}66`,
                    bgcolor: `${reviewTokens.danger}14`,
                    color: reviewTokens.danger,
                    borderRadius: `${reviewTokens.radiusSm}px`,
                    px: 0.75,
                    py: 0.25,
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: busy ? "not-allowed" : "pointer",
                    opacity: busy ? 0.5 : 1,
                  }}
                >
                  {t(messages.Reject())}
                </Box>
              </Tooltip>
            </>
          ) : null}
          {cr.status === St.ACCEPTED ? (
            <Tooltip title={restoreTooltip}>
              <span>
                <Box
                  component="button"
                  type="button"
                  disabled={busy || !eligibility.ok}
                  onClick={() => onRestore(cr)}
                  data-testid={`history-restore-${cr.id}`}
                  sx={{
                    border: `1px solid ${reviewTokens.border}`,
                    bgcolor: reviewTokens.bgPaper,
                    color: eligibility.ok
                      ? reviewTokens.textPrimary
                      : reviewTokens.textMuted,
                    borderRadius: `${reviewTokens.radiusSm}px`,
                    px: 0.75,
                    py: 0.25,
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: !eligibility.ok || busy
                      ? "not-allowed"
                      : "pointer",
                    opacity: !eligibility.ok || busy ? 0.5 : 1,
                  }}
                >
                  {t(messages.Restore())}
                </Box>
              </span>
            </Tooltip>
          ) : null}
        </Stack>
      </Box>
    </Stack>
  );
});

interface SlotProps {
  slot: HistorySlot;
  flyerPublicationStatus?: string;
  resolveSubmitter: CrSubmitterResolver;
  busyIds: string[];
  onAccept: (cr: ReviewChangeRequest) => void;
  onReject: (cr: ReviewChangeRequest) => void;
  onRestore: (cr: ReviewChangeRequest) => void;
}

const HistorySlotCard = memo(function HistorySlotCard({
  slot,
  flyerPublicationStatus,
  resolveSubmitter,
  busyIds,
  onAccept,
  onReject,
  onRestore,
}: SlotProps) {
  const { t } = useTranslation();
  const slotIdSafe = `${slot.flyerItemId}-${slot.fieldKey}`;
  const idIndex = useMemo(() => {
    const m = new Map<string, number>();
    slot.crs.forEach((cr, i) => m.set(cr.id, i));
    return m;
  }, [slot.crs]);

  const nodeCount = slot.crs.length;
  const railTopPad = 8;
  const railTotalHeight = nodeCount * 86;

  return (
    <Box
      data-testid={`history-slot-${slotIdSafe}`}
      data-slot-key={slotIdSafe}
      sx={{
        border: `1px solid ${reviewTokens.border}`,
        borderRadius: `${reviewTokens.radiusLg}px`,
        bgcolor: reviewTokens.bgPaper,
        mb: 2,
        overflow: "hidden",
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        flexWrap="wrap"
        sx={{
          px: 2,
          py: 1.25,
          borderBottom: `1px solid ${reviewTokens.border}`,
          bgcolor: reviewTokens.slate50,
          rowGap: 0.5,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            color: "text.primary",
            textTransform: "none",
            letterSpacing: 0,
            fontWeight: 700,
            fontSize: "0.8125rem",
          }}
        >
          {slot.itemName || slot.flyerItemId.slice(0, 8) + "…"}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", fontFamily: reviewTokens.fontMono }}
        >
          ·
        </Typography>
        <Box
          sx={{
            px: 0.75,
            py: 0.125,
            borderRadius: `${reviewTokens.radiusSm}px`,
            bgcolor: reviewTokens.slate100,
            fontFamily: reviewTokens.fontMono,
            fontSize: 12,
            fontWeight: 600,
            color: reviewTokens.slate700,
          }}
          title={`${t(messages.History_Slot_Header_Field())}: ${slot.fieldKey}`}
        >
          {slot.fieldKey}
        </Box>
        <Box sx={{ flex: 1 }} />
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", fontSize: 11 }}
        >
          {nodeCount}
        </Typography>
      </Stack>

      <Box
        sx={{
          position: "relative",
          px: 2,
          py: 1.5,
        }}
      >
        {slot.restoreEdges.length > 0 ? (
          <Box
            component="svg"
            sx={{
              position: "absolute",
              top: railTopPad,
              left: 16,
              width: SLOT_RAIL_WIDTH + 24,
              height: railTotalHeight,
              pointerEvents: "none",
              overflow: "visible",
            }}
          >
            {slot.restoreEdges.map((edge) => {
              const sourceIdx = idIndex.get(edge.sourceId);
              const revertIdx = idIndex.get(edge.revertId);
              if (sourceIdx == null || revertIdx == null) return null;
              const baseY = (idx: number) =>
                idx * 86 + 86 / 2;
              const y1 = baseY(sourceIdx);
              const y2 = baseY(revertIdx);
              const railX = SLOT_RAIL_WIDTH / 2;
              const arcX = railX + 22;
              const path = `M ${railX} ${y1} C ${arcX} ${y1}, ${arcX} ${y2}, ${railX} ${y2}`;
              return (
                <path
                  key={`${edge.sourceId}->${edge.revertId}`}
                  d={path}
                  fill="none"
                  stroke={reviewTokens.brand}
                  strokeWidth={1.5}
                  strokeDasharray="4 3"
                  opacity={0.7}
                />
              );
            })}
          </Box>
        ) : null}
        {slot.crs.map((cr, idx) => (
          <HistoryNode
            key={cr.id}
            cr={cr}
            isFirst={idx === 0}
            isLast={idx === nodeCount - 1}
            flyerPublicationStatus={flyerPublicationStatus}
            resolveSubmitter={resolveSubmitter}
            busy={busyIds.includes(cr.id)}
            onAccept={onAccept}
            onReject={onReject}
            onRestore={onRestore}
          />
        ))}
      </Box>
    </Box>
  );
});

export function HistoryTree({
  slots,
  flyerPublicationStatus,
  resolveSubmitter,
  busyIds,
  onAccept,
  onReject,
  onRestore,
  emptyMessage,
}: Props) {
  const { t } = useTranslation();
  if (slots.length === 0) {
    return (
      <Stack
        alignItems="center"
        spacing={1}
        sx={{ py: 6 }}
        data-testid="history-tree-empty"
      >
        <Typography variant="body2" color="text.secondary">
          {emptyMessage || t(messages.History_Empty_Title())}
        </Typography>
        <Typography variant="caption" color="text.disabled">
          {t(messages.History_Empty_Subtitle())}
        </Typography>
      </Stack>
    );
  }

  return (
    <Box data-testid="history-tree">
      <Box
        sx={{
          mb: 1.5,
          px: 1,
          py: 0.75,
          bgcolor: reviewTokens.brandSoft,
          color: reviewTokens.brandHover,
          borderRadius: `${reviewTokens.radiusSm}px`,
          fontSize: 11,
          fontWeight: 500,
        }}
      >
        {t(messages.History_Slot_Restore_Hint())}
      </Box>
      {slots.length > SLOT_VIRTUALIZE_THRESHOLD ? (
        <Virtuoso
          data={slots}
          computeItemKey={(_, slot) => `${slot.flyerItemId}-${slot.fieldKey}`}
          overscan={600}
          style={{ height: "70vh" }}
          itemContent={(_, slot) => (
            <HistorySlotCard
              slot={slot}
              flyerPublicationStatus={flyerPublicationStatus}
              resolveSubmitter={resolveSubmitter}
              busyIds={busyIds}
              onAccept={onAccept}
              onReject={onReject}
              onRestore={onRestore}
            />
          )}
        />
      ) : (
        slots.map((slot) => (
          <HistorySlotCard
            key={`${slot.flyerItemId}-${slot.fieldKey}`}
            slot={slot}
            flyerPublicationStatus={flyerPublicationStatus}
            resolveSubmitter={resolveSubmitter}
            busyIds={busyIds}
            onAccept={onAccept}
            onReject={onReject}
            onRestore={onRestore}
          />
        ))
      )}
    </Box>
  );
}
